import { ActionIcon, Box, Group, Text } from '@mantine/core';
import {
  IconCopy,
  IconRefresh,
  IconSparkles,
  IconThumbDown,
  IconThumbUp,
} from '@tabler/icons-react';
import { forwardRef, useState } from 'react';

export type ChatMessageRole = 'user' | 'provider' | 'assistant' | 'error';

export interface ChatMessageBubbleProps {
  role: ChatMessageRole;
  text: string;
}

function formatMessageTime(): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
}

/**
 * Shared visual treatment for AI Assistant messages.
 * Feedback controls are intentionally presentational until their behavior is defined.
 */
export const ChatMessageBubble = forwardRef<HTMLDivElement, ChatMessageBubbleProps>(
  function ChatMessageBubble({ role, text }, ref): JSX.Element {
    const [timestamp] = useState(formatMessageTime);
    const outbound = role === 'user' || role === 'provider';
    const assistant = role === 'assistant';

    return (
      <Box ref={ref} w="100%">
        <Group justify={outbound ? 'flex-end' : 'flex-start'} wrap="nowrap">
          <Box
            style={{ width: 'fit-content', maxWidth: '88%' }}
          >
            <Box
              px="md"
              py="sm"
              style={{
                borderRadius: 10,
                background: outbound
                  ? 'var(--mantine-color-gold-1)'
                  : role === 'error'
                    ? 'var(--mantine-color-red-0)'
                    : 'var(--mantine-color-gray-1)',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <Text
                size="sm"
                c={role === 'error' ? 'red.7' : undefined}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {text}
              </Text>
            </Box>

            <Group
              justify={outbound ? 'flex-end' : 'space-between'}
              gap={6}
              mt={4}
              px={4}
              wrap="nowrap"
            >
              <Group gap={4} c="dimmed" wrap="nowrap">
                {assistant && <IconSparkles size={12} />}
                <Text size="xs">{timestamp}</Text>
              </Group>
              {assistant && (
                <Group gap={2} wrap="nowrap" aria-label="Message actions">
                  <ActionIcon size="xs" variant="transparent" color="gray" aria-label="Copy response">
                    <IconCopy size={13} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="transparent" color="gray" aria-label="Helpful response">
                    <IconThumbUp size={13} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="transparent" color="gray" aria-label="Unhelpful response">
                    <IconThumbDown size={13} />
                  </ActionIcon>
                  <ActionIcon size="xs" variant="transparent" color="gray" aria-label="Regenerate response">
                    <IconRefresh size={13} />
                  </ActionIcon>
                </Group>
              )}
            </Group>
          </Box>
        </Group>
      </Box>
    );
  },
);
